import { Observable } from 'rxjs';

import {
    ActivityInterface,
    AdvisoryModel,
    CityInterface,
    ClientInterface,
    ClientNotificationInterface,
    ClientSignatureType,
    ContractDataInterface,
    ContractInterface,
    ContractLiteInterface,
    CountryInterface,
    CreatedContractInterface,
    DictionaryInterface,
    DisinvestmentInterface,
    DocumentInterface,
    DocumentType,
    FeeInterface,
    FileInterface,
    FullClientInterface,
    IdInterface,
    ModelInterface,
    OptimalSelectedProductInterface,
    OrderClientInterface,
    OrderDocumentInterface,
    OrderSignedInterface,
    OrderTypeAvailableInterface,
    ProductInterface,
    ProfessionalPositionInterface,
    OtherProfessionalPositionInterface,
    SelectedProductInterface,
    StreetTypeInterface,
    RoleNameInterface,
    TestInterface,
    TestResultInterface,
    TraderInterface,
    TransferInterface,
    UserInterface,
    ValidationClient,
    ValidModelInterface,
} from '../../domain/data';
import { ProductsbyOrderTypeInterface } from '../../domain/data/productsbyordertype.interface';

export abstract class ApiClientInterface {
    abstract acceptOrderSignatures(selectedProductId: IdInterface): Observable<boolean>;
    abstract acceptTestSignSignatures(clientCode: IdInterface): Observable<boolean>;
    abstract addSelectedProducts(contractCode: IdInterface, products: ProductInterface[]): Observable<SelectedProductInterface[]>;
    abstract addOrderSelectedProduct(product: SelectedProductInterface): Observable<OrderSignedInterface>;
    abstract cancelContract(contractCode: IdInterface): Observable<boolean>;
    abstract checkClient(client: ClientInterface, captcha: string): Observable<boolean>;
    abstract checkEmail(email: string, clientCode?: IdInterface): Observable<boolean>;
    abstract checkPhone(phone: string, clientCode?: IdInterface): Observable<boolean>;
    abstract checkSelectedProducts(contractCode: IdInterface, products: SelectedProductInterface[]): Observable<SelectedProductInterface[]>;
    abstract continueOrderElectronicSignature(product: SelectedProductInterface, clientCode: IdInterface): Observable<OrderSignedInterface>;
    abstract continueTestElectronicSignature(clientCode: IdInterface): Observable<OrderSignedInterface>;
    abstract createContract(contract: ContractInterface): Observable<ContractInterface>;
    abstract createContractWithProducts(
        contract: ContractInterface,
        products: ProductInterface[],
        captcha: string
    ): Observable<CreatedContractInterface>;
    abstract evaluateFee(contractCode: IdInterface, fee: FeeInterface): Observable<number>;
    abstract finalizeContract(contractCode: IdInterface): Observable<boolean>;
    abstract getActivities(): Observable<ActivityInterface[]>;
    abstract getAdvisoryModels(): Observable<AdvisoryModel[]>;
    abstract getCities(countryId: IdInterface): Observable<CityInterface[]>;
    abstract getClientByDocumentId(docType: DocumentType, docId: string): Observable<ClientInterface>;
    abstract getContract(): Observable<ContractInterface>;
    abstract getContracts(clientCode: IdInterface): Observable<ContractLiteInterface[]>;
    abstract getContractData(contractCode: IdInterface): Observable<ContractDataInterface>;
    abstract getContractSignatures(contractCode: IdInterface, clientCode?: IdInterface): Observable<OrderClientInterface[]>;
    abstract getContractDocuments(contractCode: IdInterface): Observable<DocumentInterface[]>;
    abstract getContractSignedDocuments(contractCode: IdInterface): Observable<OrderDocumentInterface[]>;
    abstract getCountries(): Observable<CountryInterface[]>;
    abstract getDictionary(): Observable<DictionaryInterface[]>;
    abstract getDocumentDetail(document: DocumentInterface): Observable<any>;
    abstract getExternalTraders(): Observable<TraderInterface[]>;
    abstract getFullClient(clientCode: IdInterface, contractCode?: IdInterface): Observable<FullClientInterface>;
    abstract getLegalDisclaimer(): Observable<string>;
    abstract getClientDocuments(clientCode: IdInterface, contractCode?: IdInterface): Observable<DocumentInterface[]>;
    abstract getModel(id: IdInterface): Observable<ModelInterface>;
    abstract getModelPdf(id: IdInterface): Observable<FileInterface>;
    abstract getMyProducts(contractCode: IdInterface): Observable<ProductInterface[]>;
    abstract getNormativeCountries(): Observable<CountryInterface[]>;
    abstract getOptimalSelectedProduct(
        contractCode: IdInterface,
        productCode: IdInterface,
        amount: number
    ): Observable<OptimalSelectedProductInterface>;
    abstract getOrderDocuments(selectedProductId: IdInterface): Observable<any>;
    abstract getOrderTypes(
        contractCode: IdInterface,
        product: SelectedProductInterface,
        clientCode?: IdInterface
    ): Observable<OrderTypeAvailableInterface[]>;
    abstract getProduct(productCode: IdInterface): Observable<ProductInterface>;
    abstract getProductDocument(productCode: IdInterface): Observable<string>;
    abstract getProducts(): Observable<ProductInterface[]>;
    abstract getProfile(): Observable<UserInterface>;
    abstract getProfessionalPositions(): Observable<ProfessionalPositionInterface[]>;
    abstract getOtherProfessionalPositions(): Observable<OtherProfessionalPositionInterface[]>;
    abstract getSelectedProducts(contractCode: IdInterface, clientCode?: IdInterface): Observable<SelectedProductInterface[]>;
    abstract getSelectedProductsSignatories(product: SelectedProductInterface, clientCode: IdInterface): Observable<OrderClientInterface[]>;
    abstract getStreetTypes(): Observable<StreetTypeInterface[]>;
    abstract getRoleName(): Observable<RoleNameInterface[]>;
    abstract getTaxClassifications(classification: string, isLegalPerson: boolean): Observable<string[]>;
    abstract getTest(clientId: IdInterface, advisoryModel: AdvisoryModel): Observable<TestInterface>;
    abstract getTestResults(clientId: IdInterface, advisoryModel: AdvisoryModel): Observable<TestResultInterface>;
    abstract getTestSignDocuments(clientCode: IdInterface): Observable<any>;
    abstract getTraders(): Observable<TraderInterface[]>;
    abstract getValidateClientNotifications(): Observable<ClientNotificationInterface[]>;
    abstract getValidModels(contract: ContractInterface, client: ClientInterface): Observable<ValidModelInterface[]>;
    abstract rejectClient(contractCode: IdInterface, clientCode: IdInterface, message: string): Observable<boolean>;
    abstract rejectContract(contractCode: IdInterface, message: string): Observable<boolean>;
    abstract removeDocument(document: DocumentInterface): Observable<boolean>;
    abstract removeOrderSelectedProduct(product: SelectedProductInterface): Observable<boolean>;
    abstract removeSelectedProduct(product: SelectedProductInterface): Observable<boolean>;
    abstract rememberOrderElectronicSignature(product: SelectedProductInterface, clientCode: IdInterface): Observable<boolean>;
    abstract reviseClient(contractCode: IdInterface, clientCode: IdInterface, message: string): Observable<boolean>;
    abstract reviseContract(contractCode: IdInterface, message: string): Observable<boolean>;
    abstract setFee(contractCode: IdInterface, fee: FeeInterface): Observable<boolean>;
    abstract setTransfer(transfer: TransferInterface): Observable<boolean>;
    abstract signDisinvestment(
        contractCode: IdInterface,
        signatories: OrderClientInterface[],
        disinvestment: DisinvestmentInterface
    ): Observable<boolean>;
    abstract signModel(contractCode: IdInterface, signatories: OrderClientInterface[], modelId: IdInterface): Observable<boolean>;
    abstract signTest(clientCode: IdInterface, signatureType: ClientSignatureType): Observable<boolean>;
    abstract signTransfer(contractCode: IdInterface, signatories: OrderClientInterface[], transfer: TransferInterface): Observable<boolean>;
    abstract updateContract(contract: ContractInterface): Observable<ContractInterface>;
    abstract updateFullClient(client: FullClientInterface): Observable<boolean>;
    abstract updateOrderSelectedProduct(product: SelectedProductInterface): Observable<OrderSignedInterface>;
    abstract updateSelectedProducts(contractCode: IdInterface, products: SelectedProductInterface[]): Observable<boolean>;
    abstract updateTest(clientId: IdInterface, test: TestInterface, requiredSignature: boolean): Observable<TestResultInterface>;
    abstract uploadClientDocument(clientCode: IdInterface, document: DocumentInterface): Observable<boolean>;
    abstract uploadContractDocument(contractCode: IdInterface, document: DocumentInterface): Observable<boolean>;
    abstract uploadSignedOrderDocument(selectedProductId: IdInterface, document: File): Observable<boolean>;
    abstract uploadTestSignDocument(clientCode: IdInterface, document: File): Observable<boolean>;
    abstract validateClient(clientCode: IdInterface): Observable<boolean>;
    abstract validateClientData(clientCode: IdInterface, validations: ValidationClient[]): Observable<boolean>;
    abstract validateClientDocument(clientCode: IdInterface, documentId: IdInterface): Observable<boolean>;
    abstract validateContract(contractCode: IdInterface): Observable<boolean>;
    abstract validateContractDocument(contractCode: IdInterface, documentId: IdInterface): Observable<boolean>;
    abstract validateNotification(contractCode: IdInterface): Observable<boolean>;


    abstract getProductsByOrderType(portfolioCode: IdInterface, clientCode?: IdInterface, distributionChannel?: string[], orderType?: string , riskLevel?: string): Observable<ProductInterface[]>;
    abstract getProductsByOrderTypeClient(portfolioCode: IdInterface, clientCode?: IdInterface, distributionChannel?: string[], orderType?: string , riskLevel?: string): Observable<ProductInterface[]>;

}
